import { useMemo } from "react";
import { useAppStore, type Language } from "../store/app";
import { de } from "./messages/de";
import { en } from "./messages/en";

/**
 * The catalogue, one module per language. `en` is the source of truth for the
 * key set; `de` is checked against it at its declaration site, so a missing
 * key fails the build (ADR-0042).
 */
export const messages = { en, de };

/** Every message key, derived from the English catalogue. */
export type MessageKey = keyof typeof en;

/**
 * Placeholder names in a message literal, in order: `"{a}–{b}"` → `"a" | "b"`.
 * The recursive right-hand side keeps scanning after the first token, so
 * multi-placeholder messages (and repeated names) derive every variable.
 */
type Vars<S extends string> =
  S extends `${string}{${infer V}}${infer R}` ? V | Vars<R> : never;

/** Variables a message key requires, or `never` when it has none. */
export type VarsFor<K extends MessageKey> = Vars<(typeof en)[K]>;

/**
 * A translator: `key` and its variables are checked together, so a placeholder
 * can neither be missed nor invented. Keys without placeholders take no second
 * argument; keys with placeholders require exactly their names (ADR-0042).
 */
export type Translate = <K extends MessageKey>(
  key: K,
  ...args: TranslateArgs<K>
) => string;

/**
 * The variables a call must pass. A literal key is checked exactly: its
 * placeholders, or nothing when it has none. A key chosen at runtime is typed
 * as the whole `MessageKey` union (route and navigation tables); its
 * placeholders cannot be derived, so it takes no variables — every key read
 * that way is variable-free.
 */
type TranslateArgs<K extends MessageKey> = [MessageKey] extends [K]
  ? []
  : VarsFor<K> extends never
    ? []
    : [vars: Record<VarsFor<K>, string | number>];

/**
 * Resolve one message and fill its `{placeholder}` tokens from `vars`.
 *
 * Numbers are stringified; dates and other formatted values stay formatted at
 * the call site (the translator is a string seam). A placeholder without a
 * supplied value is left as written. A message without placeholders is
 * returned unchanged, so `vars` stays optional for those keys.
 *
 * A development build throws if any placeholder survives — with or without
 * `vars`, so it catches a caller that bypassed the typed `Translate`
 * interface (an incomplete variable set, or a runtime key with no variables).
 *
 * @param language the catalogue to read.
 * @param key the message key, checked against the English catalogue.
 * @param vars values for the message's placeholders, if it has any.
 * @returns the message with every supplied placeholder filled.
 */
export function translate(
  language: Language,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const message = messages[language][key];
  const rendered =
    vars === undefined
      ? message
      : message.replace(PLACEHOLDER, (token: string, name: string) => {
          const value = vars[name];
          return value === undefined ? token : String(value);
        });
  if (import.meta.env.DEV) assertNoPlaceholderLeft(rendered, key);
  return rendered;
}

/**
 * Bind {@link translate} to the language currently in the app store. Memoised
 * on that language, so the returned function may enter dependency lists and
 * only changes when the language does (ADR-0042).
 *
 * @returns the translator for the current language.
 */
export function useT(): Translate {
  const language = useAppStore((state) => state.language);
  return useMemo(() => bindTranslate(language), [language]);
}

/** A placeholder token such as `{name}`, matched without nested braces. */
const PLACEHOLDER = /\{([^{}]+)\}/g;

/**
 * The dev-only completeness guard inside {@link translate}. A typed call can
 * never reach it; an untyped caller with a partial variable object — or a
 * runtime key that carries placeholders without variables — can.
 *
 * @param rendered the interpolated message.
 * @param key the message key, for the failure message.
 * @throws when placeholders remain in `rendered`.
 */
function assertNoPlaceholderLeft(rendered: string, key: MessageKey): void {
  if (!/\{[^{}]+\}/.test(rendered)) return;
  throw new Error(
    `i18n: translate("${key}") left a placeholder unfilled: ${rendered}`,
  );
}

/**
 * Build the language-bound translator behind {@link useT}.
 *
 * @param language the catalogue to bind.
 * @returns a `Translate` whose variables flow through to `translate`.
 */
function bindTranslate(language: Language): Translate {
  const t: Translate = (key, ...args) =>
    translate(language, key, args.length > 0 ? args[0] : undefined);
  return t;
}
